import { Component, EventEmitter, Input, OnInit, Output, computed, inject, signal } from '@angular/core';
import Swal from 'sweetalert2';
import { EfateStudyOptions } from 'src/app/shared/models/echo/efate-study-options';
import { EfDataSetRow } from 'src/app/shared/models/ef-data.model';
import { EFateConstants } from 'src/app/shared/utils/efate-constants';
import { EfDataStudyOptionsLogicService } from './ef-data-study-options.logic.service';

@Component({
  selector: 'app-ef-data-study-options',
  templateUrl: './ef-data-study-options.component.html',
  styleUrls: ['./ef-data-study-options.component.css']
})
export class EFDataStudyOptionsComponent implements OnInit {
  @Input({ required: true }) set dataSetRow(value: EfDataSetRow) {
    this.row.set(value);
  }

  get dataSetRow(): EfDataSetRow {
    return this.row() as EfDataSetRow;
  }

  @Input() efateSetupPk?: number;

  @Output() saved = new EventEmitter<EfateStudyOptions>();
  @Output() closed = new EventEmitter<void>();

  readonly timeUnitOptions = EFateConstants.TIME_UNIT_OPTIONS;
  readonly concentrationUnitOptions = EFateConstants.CONCENTRATION_UNIT_OPTIONS;
  readonly maxNameLength = EFateConstants.MAX_STUDY_OPTIONS_NAME_LENGTH;
  readonly maxDescriptionLength = EFateConstants.MAX_STUDY_OPTIONS_DESCRIPTION_LENGTH;

  readonly studyOptions = signal<EfateStudyOptions | undefined>(undefined);
  readonly createdDate = signal<Date | undefined>(undefined);
  readonly loading = signal<boolean>(true);
  readonly busy = signal<boolean>(false);
  readonly showValidation = signal<boolean>(false);

  readonly header = 'Study Options';

  readonly isNameValid = computed(
    () => !this.showValidation() || this.logicService.isNameValid(this.studyOptions()),
  );

  readonly isDescriptionValid = computed(
    () => !this.showValidation() || this.logicService.isDescriptionValid(this.studyOptions()),
  );

  readonly isTimeUnitValid = computed(
    () => !this.showValidation() || this.logicService.isTimeUnitValid(this.studyOptions()?.timeEnteredIn),
  );

  readonly isConcentrationUnitValid = computed(
    () => !this.showValidation() || this.logicService.isConcentrationUnitValid(this.studyOptions()?.concentrationEnteredIn),
  );

  private readonly row = signal<EfDataSetRow | undefined>(undefined);
  private readonly logicService = inject(EfDataStudyOptionsLogicService);

  async ngOnInit(): Promise<void> {
    const row = this.row();
    const savedStudyOptions = await this.logicService.getByDatasetPk(row?.efateCakeDataSetPk ?? 0);
    const studyOptions = savedStudyOptions ?? this.logicService.createDefault(row as EfDataSetRow, this.efateSetupPk);

    this.studyOptions.set(studyOptions);
    this.createdDate.set(this.logicService.parseCreatedDate(studyOptions.createdDate));
    this.loading.set(false);
  }

  onNameChange(name: string): void {
    this.patch({ name });
  }

  onDescriptionChange(description: string): void {
    this.patch({ description });
  }

  onCommentChange(comment: string): void {
    this.patch({ comment });
  }

  onTimeUnitChange(timeEnteredIn: string): void {
    this.patch({ timeEnteredIn });
  }

  onConcentrationUnitChange(concentrationEnteredIn: string): void {
    this.patch({ concentrationEnteredIn });
  }

  onCreatedDateChange(createdDate?: Date | null): void {
    this.createdDate.set(createdDate ?? undefined);
  }

  async onSave(): Promise<void> {
    this.showValidation.set(true);

    const studyOptions = this.studyOptions();
    if (!studyOptions) {
      return;
    }

    if (!this.logicService.isLinkedToDataSet(studyOptions)) {
      await this.showError('The data set must be saved before its study options can be saved. Please close the dialog, save the data set and try again.');
      return;
    }

    if (!this.logicService.isValid(studyOptions)) {
      return;
    }

    this.busy.set(true);
    const savedStudyOptions = await this.logicService.save({
      ...studyOptions,
      createdDate: this.logicService.formatCreatedDate(this.createdDate()),
    });
    this.busy.set(false);

    if (!savedStudyOptions) {
      await this.showError('The study options could not be saved. Please try again.');
      return;
    }

    this.studyOptions.set(savedStudyOptions);
    this.createdDate.set(this.logicService.parseCreatedDate(savedStudyOptions.createdDate));
    this.saved.emit(savedStudyOptions);
    this.closed.emit();
  }

  onClose(): void {
    if (this.busy()) {
      return;
    }

    this.closed.emit();
  }

  private patch(changes: Partial<EfateStudyOptions>): void {
    this.studyOptions.update((studyOptions: EfateStudyOptions | undefined) =>
      studyOptions === undefined ? studyOptions : { ...studyOptions, ...changes },
    );
  }

  private async showError(message: string): Promise<void> {
    await Swal.fire({
      title: 'Study Options',
      text: message,
      icon: 'error',
      confirmButtonColor: '#0069be',
    });
  }
}
